import { Typography } from 'antd';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { CLAIM_STATUSES, getClaimAmountVisibility } from 'all-in-one-shared-api';

import { FormattedClaimDetails } from 'utils/formatClaimDetails';
import { StyledTitle } from 'components/StyledTitle';
import { formatCurrency } from 'utils/currency';

import { TitleSection, WhiteDescriptions as Descriptions } from './styledComponents';
import { ClaimInfoCardUtils } from '../../newClaim/components/ClaimInfoCard/utils';
import { ClaimTypesEnum } from '../../claims/components/FilterSection/utils';

const { Text } = Typography;

export type ClaimInformationTableProps = Pick<
  FormattedClaimDetails,
  | 'referenceNo'
  | 'claimId'
  | 'claimStatus'
  | 'claimType'
  | 'patientName'
  | 'treatmentDate'
  | 'doctorName'
  | 'diagnosisName'
  | 'paymentMethod'
  | 'receipts'
  | 'claimTypeDescription'
  | 'sequenceNo'
  | 'paidAmount'
  | 'networkClaim'
>;

const ClaimInformationTable = ({
  referenceNo,
  claimId,
  claimStatus,
  claimType,
  patientName,
  treatmentDate,
  doctorName,
  claimTypeDescription,
  diagnosisName,
  receipts,
  paymentMethod,
  paidAmount,
  networkClaim,
}: ClaimInformationTableProps) => {
  const { t } = useTranslation();

  const { showPaidAmount, showReceiptAmount } = getClaimAmountVisibility({
    networkClaim,
    status: claimStatus,
    claimTypeCode: claimType?.code,
  });

  return (
    <div>
      <TitleSection>
        <StyledTitle level={5}>{t('claimDetails.claimInformation')}</StyledTitle>
      </TitleSection>
      <Descriptions bordered column={2}>
        {claimStatus == 'SUBMITTED' ? (
          <Descriptions.Item span={2} label={t('claimDetails.referenceNo')}>
            <Text>{`#${referenceNo}`}</Text>
          </Descriptions.Item>
        ) : (
          <Descriptions.Item span={2} label={t('claimDetails.claimNo')}>
            <Text>{`#${claimId}`}</Text>
          </Descriptions.Item>
        )}

        {patientName && (
          <Descriptions.Item span={2} label={t('claimDetails.patient')}>
            <Text>{patientName}</Text>
          </Descriptions.Item>
        )}

        {treatmentDate && (
          <Descriptions.Item
            span={2}
            label={t(ClaimInfoCardUtils.getTitleTreatmentBasedOnClaimType(claimType?.code as ClaimTypesEnum))}
          >
            <Text>{treatmentDate}</Text>
          </Descriptions.Item>
        )}

        {doctorName && (
          <Descriptions.Item span={2} label={t('claimDetails.doctorOrClinicName')}>
            <Text>{doctorName}</Text>
          </Descriptions.Item>
        )}

        {claimTypeDescription && (
          <Descriptions.Item span={2} label={t('claimDetails.claimType')}>
            <Text>{claimTypeDescription}</Text>
          </Descriptions.Item>
        )}

        {diagnosisName && (
          <Descriptions.Item span={2} label={t('claimDetails.diagnosis')}>
            <Text>{diagnosisName}</Text>
          </Descriptions.Item>
        )}

        {receipts.length > 0 && showReceiptAmount && receipts[0].amount !== '0' && (
          <Descriptions.Item span={2} label={t('claimDetails.receiptAmount')}>
            {receipts.map(({ amount, currency }, i) => (
              <Fragment key={i}>
                <Text>
                  {currency} {formatCurrency(amount, 2)}
                </Text>
                {receipts[i]?.description && <Text type="secondary">{`  ${receipts[i]?.description}`}</Text>}
                <br />
              </Fragment>
            ))}
          </Descriptions.Item>
        )}

        {paidAmount && showPaidAmount && claimStatus !== CLAIM_STATUSES.NEED_ACTION && (
          <Descriptions.Item label={t('claimDetails.paidAmount')}>
            <Text>
              {paidAmount.currency} {paidAmount.amount}
            </Text>
          </Descriptions.Item>
        )}

        {paymentMethod &&
          ![CLAIM_STATUSES.IN_PROGRESS, CLAIM_STATUSES.NEED_ACTION, CLAIM_STATUSES.DECLINED]
            .map((item) => item.toString())
            .includes(claimStatus) && (
            <Descriptions.Item label={t('claimDetails.paymentMethod')}>
              <Text>{paymentMethod}</Text>
            </Descriptions.Item>
          )}
      </Descriptions>
    </div>
  );
};
export default ClaimInformationTable;
